import { useState, FormEvent } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { ArrowRight } from 'lucide-react'
import { api } from '../lib/api'
import AdminLayout from '../components/AdminLayout'
import {
  Field,
  FieldGroup,
  FieldLabel,
  FieldSet,
} from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Button } from '@/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { SearchableSelect } from '@/components/SearchableSelect'
import { DatePicker } from '@/components/DatePicker'

type StockLevel = {
  id: number
  product: number
  product_sku: string
  product_name: string
  product_uom: string
  warehouse: number
  warehouse_code: string
  warehouse_name: string
  quantity: string
  updated_at: string
}

type Movement = {
  id: number
  product: number
  product_sku: string
  product_name: string
  movement_type: 'IN' | 'OUT' | 'TRANSFER' | 'ADJUSTMENT'
  quantity: string
  warehouse: number
  warehouse_code: string
  destination_warehouse: number | null
  destination_warehouse_code: string | null
  movement_date: string
  reference: string
  source_type: string
  source_id: number | null
  notes: string
  created_at: string
}

type Product = { id: number; sku: string; name: string; product_type: string; is_active: boolean; unit_of_measure: string }
type Warehouse = { id: number; code: string; name: string; is_active: boolean; is_default: boolean }

const TYPE_COLOR: Record<Movement['movement_type'], string> = {
  IN: 'bg-green-100 text-green-800',
  OUT: 'bg-red-100 text-red-700',
  TRANSFER: 'bg-blue-100 text-blue-800',
  ADJUSTMENT: 'bg-amber-100 text-amber-800',
}

export default function InventoryStock() {
  const qc = useQueryClient()

  const productsQ = useQuery({
    queryKey: ['inventory', 'products'],
    queryFn: async () => (await api.get<Product[]>('/inventory/products/')).data,
  })
  const warehousesQ = useQuery({
    queryKey: ['inventory', 'warehouses'],
    queryFn: async () => (await api.get<Warehouse[]>('/inventory/warehouses/')).data,
  })
  const levelsQ = useQuery({
    queryKey: ['inventory', 'stock-levels'],
    queryFn: async () => (await api.get<StockLevel[]>('/inventory/stock-levels/')).data,
  })
  const movementsQ = useQuery({
    queryKey: ['inventory', 'movements'],
    queryFn: async () => (await api.get<Movement[]>('/inventory/movements/')).data,
  })

  const [creating, setCreating] = useState(false)

  const createMut = useMutation({
    mutationFn: async (p: any) => (await api.post('/inventory/movements/', p)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['inventory', 'stock-levels'] })
      qc.invalidateQueries({ queryKey: ['inventory', 'movements'] })
      qc.invalidateQueries({ queryKey: ['inventory', 'products'] })
      setCreating(false)
    },
  })

  const products = (productsQ.data || []).filter((p) => p.is_active && p.product_type !== 'SERVICE')
  const warehouses = (warehousesQ.data || []).filter((w) => w.is_active)
  const levels = levelsQ.data || []
  const movements = movementsQ.data || []

  return (
    <AdminLayout>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-800">Stock Levels</h2>
          <p className="text-sm text-gray-500">{levels.length} positions across {warehouses.length} warehouse{warehouses.length !== 1 && 's'}</p>
        </div>
        <Button
          onClick={() => setCreating(true)}
          disabled={products.length === 0 || warehouses.length === 0}
        >
          + New movement
        </Button>
      </div>

      {(products.length === 0 || warehouses.length === 0) && (
        <div className="bg-amber-50 border border-amber-200 text-amber-800 text-sm p-3 rounded-lg mb-4">
          {products.length === 0 && <>Add at least one active product. </>}
          {warehouses.length === 0 && <>Add at least one active warehouse. </>}
        </div>
      )}

      {createMut.isError && (
        <div className="bg-red-50 border border-red-200 text-red-700 text-sm p-3 rounded-lg mb-4">
          <strong>Movement failed:</strong>{' '}
          {(() => {
            const e: any = createMut.error
            const d = e?.response?.data
            if (typeof d === 'string') return d
            return JSON.stringify(d || e?.message || e)
          })()}
        </div>
      )}

      {/* Stock levels */}
      <div className="bg-white rounded-lg shadow overflow-hidden mb-6">
        <div className="px-3 py-2 bg-gray-50 text-xs font-semibold text-gray-600 uppercase tracking-wide border-b">
          Current on-hand
        </div>
        {levelsQ.isLoading ? <p className="p-4 text-gray-500">Loading…</p> :
          levels.length === 0 ? <p className="p-4 text-gray-500">No stock yet. Create a movement to set initial quantities.</p> : (
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50 text-xs uppercase text-gray-600">
              <tr>
                <th className="px-3 py-2 text-left">Product</th>
                <th className="px-3 py-2 text-left">Warehouse</th>
                <th className="px-3 py-2 text-right">Quantity</th>
                <th className="px-3 py-2 text-left">UOM</th>
                <th className="px-3 py-2 text-left">Updated</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 text-sm">
              {levels.map((lv) => (
                <tr key={lv.id} className="hover:bg-gray-50">
                  <td className="px-3 py-2"><span className="font-mono text-xs">{lv.product_sku}</span> {lv.product_name}</td>
                  <td className="px-3 py-2"><span className="font-mono text-xs">{lv.warehouse_code}</span> {lv.warehouse_name}</td>
                  <td className={`px-3 py-2 text-right font-mono ${Number(lv.quantity) < 0 ? 'text-red-700' : ''}`}>
                    {Number(lv.quantity).toFixed(3)}
                  </td>
                  <td className="px-3 py-2 text-xs">{lv.product_uom}</td>
                  <td className="px-3 py-2 text-xs text-gray-500">{new Date(lv.updated_at).toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Movement history */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="px-3 py-2 bg-gray-50 text-xs font-semibold text-gray-600 uppercase tracking-wide border-b">
          Movement history ({movements.length})
        </div>
        {movementsQ.isLoading ? <p className="p-4 text-gray-500">Loading…</p> :
          movements.length === 0 ? <p className="p-4 text-gray-500">No movements yet.</p> : (
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50 text-xs uppercase text-gray-600">
              <tr>
                <th className="px-3 py-2 text-left">Date</th>
                <th className="px-3 py-2 text-left">Type</th>
                <th className="px-3 py-2 text-left">Product</th>
                <th className="px-3 py-2 text-right">Qty</th>
                <th className="px-3 py-2 text-left">Warehouse</th>
                <th className="px-3 py-2 text-left">Reference</th>
                <th className="px-3 py-2 text-left">Notes</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 text-sm">
              {movements.map((m) => (
                <tr key={m.id} className="hover:bg-gray-50">
                  <td className="px-3 py-2 text-xs">{m.movement_date}</td>
                  <td className="px-3 py-2">
                    <span className={`text-xs px-2 py-0.5 rounded ${TYPE_COLOR[m.movement_type]}`}>{m.movement_type}</span>
                  </td>
                  <td className="px-3 py-2"><span className="font-mono text-xs">{m.product_sku}</span> {m.product_name}</td>
                  <td className="px-3 py-2 text-right font-mono">{Number(m.quantity).toFixed(3)}</td>
                  <td className="px-3 py-2 text-xs">
                    <span className="font-mono">{m.warehouse_code}</span>
                    {m.destination_warehouse_code && (
                      <>
                        <ArrowRight className="inline h-3 w-3 mx-1 text-gray-400" />
                        <span className="font-mono">{m.destination_warehouse_code}</span>
                      </>
                    )}
                  </td>
                  <td className="px-3 py-2 text-xs font-mono">{m.reference || '—'}</td>
                  <td className="px-3 py-2 text-xs text-gray-500 truncate max-w-xs">{m.notes}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {creating && (
        <MovementDialog
          products={products}
          warehouses={warehouses}
          submitting={createMut.isPending}
          error={createMut.error as any}
          onCancel={() => setCreating(false)}
          onSubmit={(p) => createMut.mutate(p)}
        />
      )}
    </AdminLayout>
  )
}

function todayIso() { return new Date().toISOString().slice(0, 10) }

function MovementDialog({
  products, warehouses, onSubmit, onCancel, submitting, error,
}: {
  products: Product[]
  warehouses: Warehouse[]
  onSubmit: (p: any) => void
  onCancel: () => void
  submitting: boolean
  error: any
}) {
  const defaultWh = warehouses.find((w) => w.is_default) || warehouses[0]
  const [form, setForm] = useState({
    product: products[0]?.id ?? 0,
    movement_type: 'IN' as Movement['movement_type'],
    quantity: '1',
    warehouse: defaultWh?.id ?? 0,
    destination_warehouse: 0,
    movement_date: todayIso(),
    reference: '',
    notes: '',
  })
  const set = <K extends keyof typeof form>(k: K, v: typeof form[K]) =>
    setForm((f) => ({ ...f, [k]: v }))

  const submit = (e: FormEvent) => {
    e.preventDefault()
    const payload: any = { ...form }
    if (form.movement_type !== 'TRANSFER') payload.destination_warehouse = null
    onSubmit(payload)
  }

  const uom = products.find((p) => p.id === form.product)?.unit_of_measure || 'units'

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-2xl max-w-md w-full p-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">New stock movement</h3>
        <form onSubmit={submit}>
          <FieldSet>
            <FieldGroup>
              <Field>
                <FieldLabel htmlFor="mv-type">Type</FieldLabel>
                <Select
                  value={form.movement_type}
                  onValueChange={(v) => set('movement_type', v as Movement['movement_type'])}
                >
                  <SelectTrigger id="mv-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="IN">Stock in (receipt / adjustment up)</SelectItem>
                    <SelectItem value="OUT">Stock out (shipment / write-off)</SelectItem>
                    <SelectItem value="TRANSFER">Transfer between warehouses</SelectItem>
                    <SelectItem value="ADJUSTMENT">Adjustment (add)</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
              <Field>
                <FieldLabel htmlFor="mv-product">Product</FieldLabel>
                <SearchableSelect
                  id="mv-product"
                  value={form.product ? String(form.product) : undefined}
                  onChange={(v) => set('product', Number(v))}
                  placeholder="— select —"
                  options={products.map((p) => ({
                    value: String(p.id), label: p.name, hint: p.sku,
                  }))}
                />
              </Field>
              <Field>
                <FieldLabel htmlFor="mv-quantity">Quantity ({uom})</FieldLabel>
                <Input
                  id="mv-quantity"
                  type="number"
                  step="0.001"
                  required
                  min="0.001"
                  value={form.quantity}
                  onChange={(e) => set('quantity', e.target.value)}
                  className="font-mono"
                />
              </Field>
              <Field>
                <FieldLabel htmlFor="mv-warehouse">
                  {form.movement_type === 'TRANSFER' ? 'Source warehouse' : 'Warehouse'}
                </FieldLabel>
                <SearchableSelect
                  id="mv-warehouse"
                  value={form.warehouse ? String(form.warehouse) : undefined}
                  onChange={(v) => set('warehouse', Number(v))}
                  placeholder="— select —"
                  options={warehouses.map((w) => ({
                    value: String(w.id), label: w.name, hint: w.code,
                  }))}
                />
              </Field>
              {form.movement_type === 'TRANSFER' && (
                <Field>
                  <FieldLabel htmlFor="mv-dest-warehouse">Destination warehouse</FieldLabel>
                  <SearchableSelect
                    id="mv-dest-warehouse"
                    value={form.destination_warehouse ? String(form.destination_warehouse) : undefined}
                    onChange={(v) => set('destination_warehouse', Number(v))}
                    placeholder="— Select —"
                    options={warehouses.filter((w) => w.id !== form.warehouse).map((w) => ({
                      value: String(w.id), label: w.name, hint: w.code,
                    }))}
                  />
                </Field>
              )}
              <Field>
                <FieldLabel htmlFor="mv-date">Date</FieldLabel>
                <DatePicker
                  id="mv-date"
                  required
                  value={form.movement_date}
                  onChange={(v) => set('movement_date', v)}
                />
              </Field>
              <Field>
                <FieldLabel htmlFor="mv-reference">Reference</FieldLabel>
                <Input
                  id="mv-reference"
                  type="text"
                  value={form.reference}
                  onChange={(e) => set('reference', e.target.value)}
                  placeholder="SO-000123, PO-...."
                />
              </Field>
              <Field>
                <FieldLabel htmlFor="mv-notes">Notes</FieldLabel>
                <Textarea
                  id="mv-notes"
                  value={form.notes}
                  onChange={(e) => set('notes', e.target.value)}
                  rows={2}
                />
              </Field>
            </FieldGroup>
          </FieldSet>

          {error && (
            <div className="text-red-600 text-sm bg-red-50 px-3 py-2 rounded mt-3">
              {String(error?.response?.data ? JSON.stringify(error.response.data) : error.message || error)}
            </div>
          )}

          <div className="flex justify-end space-x-2 pt-4">
            <Button type="button" variant="ghost" onClick={onCancel}>Cancel</Button>
            <Button type="submit" disabled={submitting}>
              {submitting ? 'Posting…' : 'Post movement'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}
